import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	IconButton,
	Typography,
} from '@mui/material';
import { ArrowBackIcon, CloseOutlinedIcon } from 'components/icons';
import CarRegistrationAddingStage from './AddingStage';
import CarRegistrationViewingStage from './ViewingStage';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { getActiveTourKey } from 'components/onboarding/tour/activeTour';
import { setRegisterPlateFormOpen } from 'components/onboarding/tour/registerPlateTourState';

type ModalStage = 'viewing' | 'adding';

const REGISTER_PLATE_TOUR_KEY = 'canpark_register_plate';

const CarRegistrationDialog: React.FC<{
	open: boolean;
	setOpen: (open: boolean) => void;
	parkingLocation: ParkingLocation;
}> = ({ open, setOpen, parkingLocation }) => {
	const [modalStage, setModalStage] = useState<ModalStage>('viewing');

	// During the "Register a plate" tour, jump straight to the adding form when the
	// dialog opens (the tour walks the fields, not the viewing list).
	useEffect(() => {
		if (open && getActiveTourKey() === REGISTER_PLATE_TOUR_KEY) {
			setModalStage('adding');
		}
	}, [open]);

	// Report the adding form's visibility so useRegisterPlateTourSync can advance
	// the tour onto its fields (and close it out when the dialog closes).
	useEffect(() => {
		if (getActiveTourKey() !== REGISTER_PLATE_TOUR_KEY) return;
		setRegisterPlateFormOpen(open && modalStage === 'adding');
	}, [open, modalStage]);

	const handleClose = () => {
		setModalStage('viewing');
		setOpen(false);
	};

	const isViewing = modalStage === 'viewing';
	const isAdding = modalStage === 'adding';

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="xl"
			fullWidth
			aria-labelledby="car-registration-dialog-title"
			PaperProps={{
				sx: {
					borderRadius: 2,
					height: '80vh',
					padding: 3,
					overflow: 'auto',
				},
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 2,
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{isAdding && (
						<IconButton onClick={() => setModalStage('viewing')}>
							<ArrowBackIcon />
						</IconButton>
					)}
					<Typography
						id="car-registration-dialog-title"
						variant="h6"
						fontWeight={300}
					>
						{isViewing
							? `License Plate Registrations for ${parkingLocation.name}`
							: `Register license plates for ${parkingLocation.name}`}
					</Typography>
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{isViewing && (
						<Button
							variant="outlined"
							onClick={() => setModalStage('adding')}
						>
							Add New Registrations
						</Button>
					)}
					<IconButton onClick={handleClose}>
						<CloseOutlinedIcon />
					</IconButton>
				</Box>
			</Box>

			<DialogContent
				sx={{
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					minHeight: 0, // important: prevents overflow issues
				}}
			>
				{isViewing && (
					<CarRegistrationViewingStage
						parkingLocation={parkingLocation}
					/>
				)}
				{isAdding && (
					<CarRegistrationAddingStage
						parkingLocationId={parkingLocation.id}
						onSandboxComplete={handleClose}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default CarRegistrationDialog;
